import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts'
import Card from '../../components/ui/Card'
import { formatCurrency } from '../../lib/format'

function ProfitChart({ transactions, title = 'Monthly Profit/Loss', months = 12 }) {
  // Generate last N months
  const now = new Date()
  const monthsData = []

  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const monthLabel = date.toLocaleDateString('en-US', { month: 'short' })

    monthsData.push({
      month: monthKey,
      label: monthLabel,
      revenue: 0,
      expenses: 0,
      profit: 0,
    })
  }

  // Aggregate transactions by month
  transactions.forEach(t => {
    const date = new Date(t.date)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const monthData = monthsData.find(m => m.month === monthKey)

    if (monthData) {
      const amount = parseFloat(t.amount)
      if (t.type === 'revenue' || t.type === 'income') {
        monthData.revenue += amount
      } else if (t.type === 'expense') {
        monthData.expenses += amount
      }
    }
  })

  // Calculate profit for each month
  monthsData.forEach(m => {
    m.profit = m.revenue - m.expenses
  })

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div style={{
          background: 'white',
          padding: '12px',
          border: '1px solid var(--gray-200)',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: '0 0 8px', fontWeight: 500 }}>{label}</p>
          <p style={{ margin: '2px 0', color: '#10b981' }}>
            Revenue: {formatCurrency(data.revenue)}
          </p>
          <p style={{ margin: '2px 0', color: '#ef4444' }}>
            Expenses: {formatCurrency(data.expenses)}
          </p>
          <p style={{
            margin: '8px 0 0',
            fontWeight: 600,
            color: data.profit >= 0 ? '#10b981' : '#ef4444'
          }}>
            Profit: {formatCurrency(data.profit)}
          </p>
        </div>
      )
    }
    return null
  }

  const formatYAxis = (value) => {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`
    return `$${value}`
  }

  return (
    <Card>
      <h3 className="card-title mb-4">{title}</h3>

      <div style={{ width: '100%', height: 250 }}>
        <ResponsiveContainer>
          <BarChart
            data={monthsData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12, fill: 'var(--gray-500)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--gray-200)' }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12, fill: 'var(--gray-500)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--gray-200)' }}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="var(--gray-300)" />
            <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
              {monthsData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.profit >= 0 ? '#10b981' : '#ef4444'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

export default ProfitChart
